/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import { Modal } from '@/components/ui'
import { useApi, useDashboard } from '@/hooks'
import { convertCelsius, tempUnitSuffix, userToCelsius } from '@/utils/conversions'

import { putLocationRange } from './api'
import { useColdStorage } from './ColdStorageContext'
import type { Units } from './format'
import { TemperatureRangeSlider } from './TemperatureRangeSlider'
import type { Threshold } from './types'

const DEFAULT_RANGE: Threshold = { lowCelsius: -25, highCelsius: -15 }

/**
 * Set the acceptable temperature range of a location. Every temperature sensor of the
 * location is judged against it, on this screen and on the legacy in-store screen, which
 * reads the same two columns. Opened from the location row and from the sensor modal.
 */
export const LocationRangeModal = () => {
  const { api } = useApi()
  const context = useColdStorage()
  const units = useDashboard().configuration.units as Units
  const asset = context.assets.find((a) => a.id === context.rangeLocationId)
  const show = !!asset

  const [range, setRange] = useState<Threshold>(DEFAULT_RANGE)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (asset) setRange(asset.threshold ?? DEFAULT_RANGE)
  }, [asset])

  if (!asset) return null

  const suffix = tempUnitSuffix(units)
  const close = () => context.setRangeLocationId(undefined)

  const save = async () => {
    setSaving(true)
    try {
      await putLocationRange(api, asset.id, range)
      await context.invalidateState()
      context.notify({
        header: 'Range saved',
        body: `${asset.name} now alarms outside the new range.`,
        position: 'end',
        success: true,
      })
      close()
    } catch {
      context.notify({
        header: 'Range not saved',
        body: 'Something went wrong. Please try again.',
        position: 'end',
        warning: true,
      })
    } finally {
      setSaving(false)
    }
  }

  const numberInput = (label: string, key: keyof Threshold) => (
    <label className="form-control">
      <span className="label-text text-xs">
        {label} ({suffix})
      </span>
      <input
        type="number"
        aria-label={label}
        className="input input-bordered input-sm w-28"
        value={Math.round(convertCelsius(range[key], units))}
        onChange={(e) => {
          const value = Number(e.target.value)
          if (Number.isFinite(value)) setRange({ ...range, [key]: userToCelsius(value, 1, units) })
        }}
      />
    </label>
  )

  return (
    <Modal
      title={`Acceptable range · ${asset.name}`}
      show={show}
      onHide={close}
      maxWidth="520px"
      footerButtons={[
        { label: 'Cancel', onClick: close, variant: 'default', class: 'btn-ghost' },
        {
          label: 'Save',
          onClick: () => void save(),
          useModalVariant: true,
          isLoading: saving,
          disabled: saving || range.lowCelsius >= range.highCelsius,
        },
      ]}
      bodyContent={
        <div className="space-y-4">
          <p className="text-sm text-base-content/70">
            Every temperature sensor in {asset.name} shows red when its reading is outside this range.
          </p>
          <TemperatureRangeSlider
            lowCelsius={range.lowCelsius}
            highCelsius={range.highCelsius}
            onChange={setRange}
            units={units}
          />
          <div className="flex items-end gap-4">
            {numberInput('Low', 'lowCelsius')}
            {numberInput('High', 'highCelsius')}
            {range.lowCelsius >= range.highCelsius && (
              <span className="text-xs text-error">Low must be below high.</span>
            )}
          </div>
        </div>
      }
    />
  )
}
